import React from 'react';
import { Skeleton, Tag, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { InhubKpiCell, InhubWowSeverity } from '../graphql/types';

interface Props {
    label: string;
    cell: InhubKpiCell;
    unit?: string;
    description?: React.ReactNode;
    definitionTooltip?: React.ReactNode;
    onClick?: () => void;
    onDrilldown?: () => void;
    footer?: React.ReactNode;
    selected?: boolean;
    loading?: boolean;
}

const bgBySeverity: Record<InhubWowSeverity, string> = {
    NONE: 'bg-violet-50',
    WARN: 'bg-yellow-50',
    DANGER: 'bg-red-50'
};

const borderBySeverity: Record<InhubWowSeverity, string> = {
    NONE: 'border-violet-200/70',
    WARN: 'border-yellow-200',
    DANGER: 'border-red-200'
};

const KpiCardWithWow: React.FC<Props> = ({ label, cell, unit, description, definitionTooltip, onClick, onDrilldown, footer, selected, loading }) => {
    const { t } = useTranslation();
    const severity: InhubWowSeverity = cell.severity ?? 'NONE';
    const isFreshT1 = cell.freshness === 'T1';
    const wowIsZero = cell.wowPercent === 0;
    const wowSign = cell.wowPercent > 0 ? '+' : '';
    const isBadTrend = cell.isNegativeIndicator ? cell.wowPercent > 0 : cell.wowPercent < 0;
    const wowColorClass = severity === 'DANGER'
        ? 'text-red-500'
        : severity === 'WARN'
            ? 'text-yellow-600'
            : wowIsZero
                ? 'text-gray-600'
                : isBadTrend
                    ? 'text-red-500'
                    : 'text-green-600';
    const borderClass = selected ? 'border-purple-500 ring-1 ring-purple-200' : borderBySeverity[severity];

    return (
        <div
            className={`flex flex-col gap-1.5 rounded-lg shadow-sm border px-4 py-3 w-full h-full ${borderClass} ${bgBySeverity[severity]} ${onClick ? 'cursor-pointer hover:border-purple-400 transition-colors' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-semibold">
                    {label}
                    {definitionTooltip && (
                        <Tooltip title={definitionTooltip}>
                            <InfoCircleOutlined className="ml-1 text-gray-400" onClick={(e) => e.stopPropagation()} />
                        </Tooltip>
                    )}
                    {(unit || description) && (
                        <span className="text-xs text-gray-500 ml-0.5 font-normal">
                            ({unit}
                            {unit && description ? ', ' : ''}
                            {description})
                        </span>
                    )}
                </span>
                <Tag
                    color={isFreshT1 ? 'orange' : 'green'}
                    className="m-0 text-xs shrink-0"
                    bordered={false}
                >
                    {isFreshT1 ? t('wms.delivery.inhubDashboard.badge.t1') : t('wms.delivery.inhubDashboard.badge.realtime')}
                </Tag>
            </div>
            {loading ? (
                <Skeleton active paragraph={false} title={{ width: '60%' }} />
            ) : (
                <>
                    <div className="flex items-end gap-1">
                        {onDrilldown && cell.value > 0 ? (
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDrilldown(); }}
                                style={{ color: '#1890ff' }}
                                className="text-2xl font-semibold"
                            >
                                {Number(cell.value).toLocaleString()}
                            </a>
                        ) : (
                            <span className="text-2xl font-semibold text-gray-900">{Number(cell.value).toLocaleString()}</span>
                        )}
                        {onClick && (
                            <span className="text-gray-300 text-base mb-1">
                                <RightOutlined />
                            </span>
                        )}
                    </div>
                    <div className="text-xs">
                        <span className={wowColorClass}>
                            {!wowIsZero && (cell.wowPercent > 0 ? <ArrowUpOutlined className="mr-0.5" /> : <ArrowDownOutlined className="mr-0.5" />)}
                            {wowSign}{cell.wowPercent.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                        </span>
                        <span className="ml-1 text-gray-500">({t('wms.delivery.inhubDashboard.kpi.wowChange')})</span>
                        {severity === 'DANGER' && <span className="ml-1 text-red-500">·{t('wms.delivery.inhubDashboard.kpi.exceedThreshold')}</span>}
                        {severity === 'WARN' && <span className="ml-1 text-yellow-600">·{t('wms.delivery.inhubDashboard.kpi.exceedThreshold')}</span>}
                    </div>
                </>
            )}
            {footer && (
                <div className="-mx-4 -mb-3 mt-1 px-4 py-2 bg-purple-50 border-t border-purple-100 rounded-b-lg">
                    {footer}
                </div>
            )}
        </div>
    );
};

export default KpiCardWithWow;
